/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import React, {
  useState, useContext, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { X } from 'lucide-react';
import { ChipSelectSearch } from '../../../components/muiCustomized/ChipSelectSearchable';
import Toggle from '../../../components/Toggle';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import { NotificationContext } from '../../../contexts/Notification';
import { screenshotsPerPeriodMapping, blurScreenshotsMapping } from '../../../constants/screenshotsPerPeriod';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../../utils/styledSelect';
import { DESIGNATIONS } from '../../../constants/designation';

function EditModal({
  member,
  closeModal,
  onMemberSaved,
  workspaceId,
  teams,
  ptoPolicies,
}) {
  const { displayNotification } = useContext(NotificationContext);
  const [isSaving, setIsSaving] = useState(false);
  const [designation, setDesignation] = useState(member?.designation ?? '');
  const [assignedTeams, setAssignedTeams] = useState([]);
  const [currentTeams, setCurrentTeams] = useState([]);
  const [screenshotFrequency, setScreenshotFrequency] = useState(member.screenshotsPerPeriod);
  const [blurScreenshots, setBlurScreenshots] = useState(member.blurScreenshots);
  const [webcamAuditsPerPeriod, setWebcamAuditsPerPeriod] = useState(member.webcamAuditsPerPeriod);
  const [ptoPolicy, setPtoPolicy] = useState(member.offTimePolicies[0]?.id);
  const [formErrors, setFormErrors] = useState({});

  const isAssignedTeamsChanged = () => {
    let isEqual = currentTeams.every((team) => assignedTeams.some((obj) => obj.id === team.id));
    if (isEqual) {
      isEqual = assignedTeams.every((team) => currentTeams.some((obj) => obj.id === team.id));
    }
    return !isEqual;
  };

  const isChanged = useMemo(() => {
    const isTeamsChanged = isAssignedTeamsChanged();
    return isTeamsChanged || designation !== member.designation
      || screenshotFrequency !== member.screenshotsPerPeriod
      || blurScreenshots !== member.blurScreenshots
      || webcamAuditsPerPeriod !== member.webcamAuditsPerPeriod
      || ptoPolicy !== member.offTimePolicies[0]?.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designation, assignedTeams, screenshotFrequency, blurScreenshots, webcamAuditsPerPeriod, ptoPolicy]);

  useEffect(() => {
    const memberTeams = member.teamMates.map((teamMate) => (
      { id: teamMate.team.id, name: teamMate.team.name }
    ));
    setAssignedTeams(memberTeams);
    setCurrentTeams(memberTeams);
  }, [member]);

  const handleSaveMember = async () => {
    setIsSaving(true);
    const params = {};
    // Always send assignedTeams: the management endpoint calls
    // assignedTeams.map(...) unconditionally, so omitting it 500s when only a
    // non-team field changed. Sending the current list keeps it an array.
    params.assignedTeams = assignedTeams;
    if (designation !== member?.designation) params.designation = designation;
    if (screenshotFrequency !== member.screenshotsPerPeriod) params.screenshotsPerPeriod = screenshotFrequency;
    if (blurScreenshots !== member.blurScreenshots) params.blurScreenshots = blurScreenshots;
    if (webcamAuditsPerPeriod !== member.webcamAuditsPerPeriod) {
      params.webcamAuditsPerPeriod = webcamAuditsPerPeriod;
    }
    if (ptoPolicy !== member?.offTimePolicies[0]?.id) params.ptoPolicy = ptoPolicy;
    const { data, success } = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.memberManagement(workspaceId, member.id),
      data: {
        ...params,
      },
    });
    if (success) {
      onMemberSaved();
      closeModal();
      displayNotification({
        message: 'Changes saved successfully.',
        severity: 'success',
      });
    } else {
      setFormErrors(data);
      displayNotification({
        message: 'Error occurs while saving changes.',
        severity: 'error',
      });
    }
    setIsSaving(false);
  };

  const teamList = Array.isArray(teams) ? teams : [];
  const designationOptions = DESIGNATIONS.map((d) => d.name);
  const labelCls = 'block text-sm font-medium text-ink-900 mb-2';

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c">
      <Box style={{ width: 520, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, fontFamily: 'Inter, sans-serif' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-rule" style={{ borderBottomStyle: 'solid' }}>
          <h5 className="text-lg font-semibold leading-none text-ink-900 truncate">{`Edit ${member?.user?.fullName || ''}`}</h5>
          <button type="button" aria-label="Close" onClick={closeModal} className="text-ink-500 hover:text-ink-900 bg-transparent border-0 cursor-pointer p-0 flex-shrink-0">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-4">
          <div>
            <span className="block text-sm font-medium text-ink-900 mb-1.5">Designation</span>
            <select
              value={designation || ''}
              onChange={(e) => setDesignation(e.target.value)}
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={STYLED_SELECT_STYLE}
            >
              <option value="">Select designation</option>
              {designationOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            {formErrors?.designation ? <p className="text-xs text-[#CC4525] mt-1.5">{formErrors.designation}</p> : null}
          </div>

          <ChipSelectSearch
            redesign
            id="teams"
            label="Teams Assigned"
            placeholder="Search and select teams"
            selectAllBtn
            count={`${assignedTeams.length}/${teamList.length}`}
            selectedMembers={assignedTeams}
            members={teamList}
            handleChange={(e) => setAssignedTeams(e.target.value)}
            handleSelectAll={() => setAssignedTeams([...teamList])}
          />

          <div>
            <span className={labelCls}>Control the number of screenshots taken in a 10-minute period</span>
            <Toggle
              activeLabel={Object.keys(screenshotsPerPeriodMapping).find(
                (key) => screenshotsPerPeriodMapping[key] === screenshotFrequency,
              )}
              options={[
                { label: 'None', onClick: () => { setScreenshotFrequency(0); } },
                { label: '1x', onClick: () => { setScreenshotFrequency(1); } },
                { label: '2x', onClick: () => { setScreenshotFrequency(2); } },
                { label: '3x', onClick: () => { setScreenshotFrequency(3); } },
              ]}
            />
          </div>

          <div>
            <span className={labelCls}>Automatically blur screenshots upon capture</span>
            <Toggle
              activeLabel={Object.keys(blurScreenshotsMapping).find(
                (key) => blurScreenshotsMapping[key] === blurScreenshots,
              )}
              options={[
                { label: 'Off', onClick: () => { setBlurScreenshots(false); } },
                { label: 'On', onClick: () => { setBlurScreenshots(true); } },
              ]}
            />
          </div>

          <div>
            <span className={labelCls}>Control the webcam audit for the user</span>
            <Toggle
              activeLabel={webcamAuditsPerPeriod}
              options={[
                { label: 'None', onClick: () => { setWebcamAuditsPerPeriod('None'); } },
                { label: 'Hourly', onClick: () => { setWebcamAuditsPerPeriod('Hourly'); } },
                { label: 'Daily', onClick: () => { setWebcamAuditsPerPeriod('Daily'); } },
                { label: 'Weekly', onClick: () => { setWebcamAuditsPerPeriod('Weekly'); } },
              ]}
            />
          </div>

          <div>
            <span className="block text-sm font-medium text-ink-900 mb-1.5">PTO Policy</span>
            <select
              value={ptoPolicy ?? ''}
              onChange={(e) => setPtoPolicy(Number(e.target.value))}
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={STYLED_SELECT_STYLE}
            >
              {ptoPolicies.map((policy) => (
                <option key={policy.id} value={policy.id}>{policy.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-2 px-6 pb-5 pt-1">
          <button type="button" onClick={closeModal} className="inline-flex items-center justify-center box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>Cancel</button>
          <button type="button" onClick={handleSaveMember} disabled={isSaving || !isChanged} className="inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition">
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

EditModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  onMemberSaved: PropTypes.func.isRequired,
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    designation: PropTypes.string,
    screenshotsPerPeriod: PropTypes.number,
    webcamAuditsPerPeriod: PropTypes.string,
    blurScreenshots: PropTypes.bool,
    offTimePolicies: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
    })),
    teamMates: PropTypes.arrayOf(PropTypes.shape({
      team: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    })),
    user: PropTypes.shape({
      fullName: PropTypes.string,
    }),
  }).isRequired,
  workspaceId: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  teams: PropTypes.array,
  ptoPolicies: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  })),
};

EditModal.defaultProps = {
  teams: [],
  ptoPolicies: [],
};

export default EditModal;
